import { Assets, Container, Sprite, Texture, VideoSource, type Ticker } from 'pixi.js';
import type { LayoutProfile, PresenterLayout, ResolvedTheme } from '../types';
import { pickMascotCodec } from './mascot-codec';
import { Timeline } from './tween';

/**
 * Animated theme mascot rendered inside the Pixi scene (alpha video → video textures), so the reels,
 * lines and overlays can draw on top of it (portrait puts the reel window over the mascot's legs).
 * Idle loops forever; win/loss play one cycle at t0 and cross-fade back to idle; a new spin interrupts.
 *
 * Loading order: the transparent poster shows as soon as it arrives (a ~150 KB still, long before the first
 * 0.3–2 MB clip has downloaded) and stays until the idle clip has decoded and really started playing — so a slow
 * network, a blocked autoplay (iOS Low Power Mode; the first spin retries) or a missing codec never leaves the slot
 * empty or opaque. Codec: VP9-alpha WebM everywhere except WebKit, which drops the WebM alpha plane and gets the
 * HEVC-with-alpha `.mov` variant instead (see mascot-codec.ts). Reduced-motion users keep the poster.
 *
 * Texture path: the video element is a Pixi VideoSource and Pixi re-uploads frames from its shared ticker. This
 * only works where VP9-alpha WebM plays (Chromium, Firefox); WebKit gets the DOM route instead (mascot-dom.ts),
 * and this layer shows just the poster there.
 */
export interface MascotLayerOptions {
  theme: ResolvedTheme;
  ticker: Ticker;
  reducedMotion?: boolean;
}

type Clip = 'idle' | 'win' | 'loss';
const FADE_MS = 140;
const UPDATE_FPS = 24;


export class MascotLayer extends Container {
  private theme: ResolvedTheme;
  private readonly tl: Timeline;
  private readonly reducedMotion: boolean;
  private readonly sprites: Partial<Record<Clip, Sprite>> = {};
  private readonly videos: Partial<Record<Clip, HTMLVideoElement>> = {};
  private readonly sources: Partial<Record<Clip, VideoSource>> = {};
  /** Natural pixel size per sprite: clips and poster may differ in resolution but share one aspect and foot anchor. */
  private readonly natural = new Map<Sprite, { w: number; h: number }>();
  private poster: Sprite | null = null;
  private posterUrl: string | null = null;
  private box: LayoutProfile['mascot'] | null = null;
  private reaction = 0;
  private disposed = false;
  private shown = false;
  /** The idle clip is playing on screen and the poster has been retired. */
  private live = false;
  private readonly onGesture = () => {
    if (!this.live) this.startIdle();
  };
  private readonly onVisibility = () => {
    if (!document.hidden && this.shown) this.startIdle();
  };
  /** Last events of this layer (bounded), readable from the debug handle for device diagnostics. */
  readonly trace: string[] = [];

  constructor(o: MascotLayerOptions) {
    super();
    this.label = 'mascot';
    this.theme = o.theme;
    this.tl = new Timeline(o.ticker);
    this.reducedMotion = !!o.reducedMotion;
    this.visible = false;
    this.eventMode = 'none';
    for (const ev of ['pointerdown', 'touchstart', 'keydown'] as const) document.addEventListener(ev, this.onGesture, { capture: true, passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);
    void this.load();
  }

  get available(): boolean {
    return !!this.theme.assets.mascot?.idle;
  }

  private mark(event: string): void {
    this.trace.push(`${Math.round(performance.now())} ${event}`);
    if (this.trace.length > 40) this.trace.shift();
  }

  private async load(): Promise<void> {
    const m = this.theme.assets.mascot;
    if (!m?.idle) return;
    const posterReady = this.loadPoster(m.poster);
    const codec = this.reducedMotion ? null : pickMascotCodec(m);
    this.mark(`codec ${codec ?? 'poster-only'}${this.reducedMotion ? ' (reduced motion)' : ''}`);
    if (!codec) return posterReady;
    if (codec !== 'vp9') return posterReady;
    const clips = m;
    await this.make('idle', clips.idle, true);
    if (this.disposed) return;
    if (!this.sprites.idle) {
      // The decoder refused the clip: the poster stays.
      this.mark('idle decode failed');
      this.teardownVideos();
      return posterReady;
    }
    this.startIdle();
    // Reactions preload in the background; they are optional.
    if (clips.win) void this.make('win', clips.win, false);
    if (clips.loss) void this.make('loss', clips.loss, false);
  }

  private make(clip: Clip, src: string, loop: boolean): Promise<void> {
    const video = document.createElement('video');
    video.muted = true;
    video.defaultMuted = true;
    video.loop = loop;
    video.playsInline = true;
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    video.src = src;
    this.videos[clip] = video;
    // Whatever started the idle clip, its first real playback retires the poster.
    video.addEventListener('playing', () => this.mark(`${clip} playing t=${video.currentTime.toFixed(2)}`));
    video.addEventListener('pause', () => this.mark(`${clip} pause t=${video.currentTime.toFixed(2)}`));
    if (loop) video.addEventListener('playing', () => this.goLive(video));
    // iOS fetches media data only once playback is requested (preload is ignored): a muted inline play() is allowed
    // without a gesture and merely rejected otherwise (then the first spin retries while the poster stays).
    // Reaction clips are paused again on their first decoded frame; they stay invisible (alpha 0) meanwhile.
    this.kick(video);
    return new Promise<void>((resolve) => {
      const finish = () => {
        video.removeEventListener('loadeddata', finish);
        video.removeEventListener('error', finish);
        if (this.disposed || video.error || !video.videoWidth) return resolve();
        this.mark(`${clip} loadeddata rs=${video.readyState} ${video.videoWidth}x${video.videoHeight}`);
        const source = this.videoSource(video, loop);
        this.sources[clip] = source;
        const sprite = new Sprite(new Texture({ source }));
        sprite.anchor.set(0.5, 1);
        sprite.alpha = 0;
        this.sprites[clip] = sprite;
        this.natural.set(sprite, { w: video.videoWidth, h: video.videoHeight });
        this.addChild(sprite);
        // Reaction clips sit paused until t0; the idle sprite must stay on top of them in draw order.
        if (clip !== 'idle' && this.sprites.idle) this.setChildIndex(this.sprites.idle, this.children.length - 1);
        this.applyLayout();
        if (!loop) {
          video.pause();
          try {
            video.currentTime = 0;
          } catch {
            /* not seekable yet */
          }
        } else if (!this.shown) video.pause();
        else this.goLive(video);
        resolve();
      };
      if (video.readyState >= 2) finish();
      else {
        video.addEventListener('loadeddata', finish);
        video.addEventListener('error', finish);
      }
    });
  }

  /** Chromium/Firefox: the video element is the texture resource; Pixi re-uploads frames itself. */
  private videoSource(video: HTMLVideoElement, loop: boolean): VideoSource {
    // Create the GPU texture only once a decoded frame exists: uploading a metadata-only video
    // leaves the GL texture mis-sized and every later frame upload overflows (clip renders empty).
    const source = new VideoSource({
      resource: video,
      autoLoad: false,
      autoPlay: false,
      updateFPS: UPDATE_FPS,
      loop,
      muted: true,
      playsinline: true,
      alphaMode: 'premultiply-alpha-on-upload',
    });
    // Pixi's load() ends with a VP9-alpha probe (a tiny WebM data URL) that never settles on WebKit builds
    // which parse WebM but cannot decode it. Everything the texture needs — play/pause/canplay listeners,
    // resize, frame uploads — is wired before that probe, so start it without waiting for it.
    source.load().catch(() => undefined);
    source.resize(video.videoWidth, video.videoHeight);
    return source;
  }

  private async loadPoster(url: string | undefined): Promise<void> {
    if (!url || this.disposed) return;
    try {
      const tex = await Assets.load<Texture>(url);
      if (this.disposed) return;
      this.posterUrl = url;
      this.poster = new Sprite(tex);
      this.poster.anchor.set(0.5, 1);
      this.poster.visible = !this.live;
      this.natural.set(this.poster, { w: tex.width, h: tex.height });
      this.addChildAt(this.poster, 0);
      this.applyLayout();
    } catch {
      /* no poster: the clip shows when it arrives, or nothing */
    }
  }

  private teardownVideos(): void {
    for (const clip of Object.keys(this.videos) as Clip[]) {
      const v = this.videos[clip];
      const s = this.sprites[clip];
      const src = this.sources[clip];
      if (v) {
        v.pause();
        v.removeAttribute('src');
        v.load();
      }
      if (s) this.natural.delete(s);
      s?.destroy({ texture: false });
      src?.destroy();
      delete this.videos[clip];
      delete this.sprites[clip];
      delete this.sources[clip];
    }
    this.live = false;
    if (this.poster) this.poster.visible = true;
  }

  private kick(v: HTMLVideoElement): void {
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch((e: unknown) => this.mark(`kick rejected ${(e as Error)?.name ?? ''}`));
  }

  private play(v: HTMLVideoElement | undefined, started?: () => void): void {
    if (!v || !this.shown) return;
    const p = v.play();
    if (p && typeof p.then === 'function') p.then(() => started?.(), () => undefined);
    else started?.();
  }

  private startIdle(): void {
    const v = this.videos.idle;
    if (!v || !this.sprites.idle) return;
    this.play(v, () => this.goLive(v));
  }

  /** First real playback of the idle clip: fade it in over the poster, then drop the poster. */
  private goLive(v: HTMLVideoElement): void {
    if (this.live || this.disposed || !this.shown || v !== this.videos.idle || !this.sprites.idle || v.paused) return;
    this.live = true;
    this.mark('live');
    this.fadeTo('idle');
    setTimeout(() => {
      if (!this.disposed && this.live && this.poster) this.poster.visible = false;
    }, FADE_MS + 40);
  }

  /** Fit the clip into the profile's mascot box (contain, anchored bottom-centre). Hidden when the profile has no box. */
  layout(profile: LayoutProfile, _info?: PresenterLayout | null): void {
    this.box = profile.mascot ?? null;
    this.applyLayout();
  }

  private applyLayout(): void {
    const on = !!this.box && this.available && this.natural.size > 0;
    if (this.visible !== on) this.visible = on;
    if (!on) {
      if (this.shown) {
        this.shown = false;
        this.mark('hidden (no box)');
        for (const v of Object.values(this.videos)) v?.pause();
      }
      return;
    }
    const b = this.box!;
    for (const [sp, n] of this.natural) {
      sp.scale.set(Math.min(b.width / n.w, b.height / n.h));
      sp.position.set(b.x + b.width / 2, b.y + b.height);
    }
    if (!this.shown) {
      this.shown = true;
      this.mark(`shown box=${b.x},${b.y},${b.width}x${b.height}`);
      this.startIdle();
    }
  }

  private fadeTo(clip: Clip): void {
    for (const [k, sp] of Object.entries(this.sprites)) {
      if (!sp) continue;
      const from = sp.alpha;
      const to = k === clip ? 1 : 0;
      if (from === to) continue;
      void this.tl.tween({ duration: FADE_MS, update: (t) => (sp.alpha = from + (to - from) * t) });
    }
  }

  /** Back to the idle loop, interrupting any reaction (called when a spin starts). */
  idle(): void {
    this.reaction++;
    if (!this.videos.idle) return;
    this.startIdle();
    if (this.live) this.fadeTo('idle');
    for (const k of ['win', 'loss'] as const) {
      const v = this.videos[k];
      if (!v) continue;
      setTimeout(() => {
        if (this.disposed || this.sprites[k]?.alpha === 1) return;
        v.pause();
      }, FADE_MS + 40);
    }
  }

  /** One cycle of a reaction clip at t0, then idle. Silently ignored when the clip is missing or not ready. */
  react(kind: 'win' | 'loss'): void {
    const v = this.videos[kind];
    this.mark(`react ${kind}${!v ? ' (no clip)' : !this.live ? ' (not live)' : v.readyState < 2 ? ' (not ready)' : ''}`);
    if (!v || !this.shown || !this.live || v.readyState < 2) return;
    const id = ++this.reaction;
    try {
      v.currentTime = 0;
    } catch {
      /* not seekable yet */
    }
    this.play(v);
    this.fadeTo(kind);
    const done = () => {
      v.removeEventListener('ended', done);
      if (id !== this.reaction || this.disposed) return;
      this.idle();
    };
    v.addEventListener('ended', done);
    const ms = Number.isFinite(v.duration) && v.duration > 0 ? v.duration * 1000 + 250 : 4000;
    setTimeout(() => {
      if (id === this.reaction && !this.disposed && this.sprites[kind]?.alpha === 1) done();
    }, ms);
  }

  override destroy(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.reaction++;
    for (const ev of ['pointerdown', 'touchstart', 'keydown'] as const) document.removeEventListener(ev, this.onGesture, { capture: true });
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.tl.destroy();
    this.teardownVideos();
    this.natural.clear();
    if (this.posterUrl) void Assets.unload(this.posterUrl).catch(() => undefined);
    super.destroy({ children: true });
  }
}
